import React, { ReactNode } from 'react';
import { I18n, Translate } from 'react-redux-i18n';
import {
  Route,
  RouteComponentProps,
  Switch,
  withRouter,
} from 'react-router-dom';
import btnFilter from '../../../img/accountManagement/btn_filtrer.svg';
import btnFilterOuvert from '../../../img/accountManagement/btn_filtrer_ouvert.svg';
import btnInfo from '../../../img/accountManagement/btn_infos_collaborateurs.svg';
import collaborators from '../../../img/icones/comptes_collaborateurs.svg';
import compteApi from '../../../network/api/compteApi';
import invitationApi from '../../../network/api/invitationApi';
import { Entity, TypeEntityEnum } from '../../../network/api/types/compte/Entity';
import { ListMember } from '../../../network/api/types/compte/ListMember';
import { MemberDTO } from '../../../network/api/types/compte/MemberDTO';
import { UserDTO } from '../../../network/api/types/compte/UserDTO';
import {
  InvitationBasicDTO,
  InvitationDTO,
  InvitationPopinEnum,
} from '../../../network/api/types/InvitationDTO';
import {
  COLLABORATORS_MANAGEMENT,
  COLLABORATORS_MANAGEMENT_COLLAB_INFO_DRAWER,
  HOME_PAGE_ROUTE,
} from '../../../routes';
import { getInfos } from '../../../services/userService';
import { getStructureName } from '../../../services/utils';
import Button from '../../common/Button';
import H4Title from '../../common/H4Title';
import BasicCheckBox from '../../common/inputs/BasicCheckBox';
import SearchButton from '../../common/inputs/SearchButton';
import Drawer from '../../common/modals/Drawer';
import NavigationBackButton from '../../common/NavigationBackButton';
import { toastError, toastSuccess } from '../../common/Notification';
import CloseableComponent from '../../external/CloseableComponent';
import Footer from '../../layout/Footer';
import Loader from '../../Loader';
import CollaboratorsInformation from '../home/myProInfo/CollaboratorsInformation/CollaboratorsInformation';
import CollaboratorTile from './components/CollaboratorTile';
import ConfirmationModal from './components/ConfirmationModal';
import GestionnaireTile from './components/GestionnaireTile';
import InvitationModal from './components/InvitationModal';
import InvitationTile from './components/InvitationTile';

interface MatchParams {
  entiteId: string;
}

type FilterField =
  'gestionnaire'
  | 'gestionnaireDelegate'
  | 'noRight';

type Filter = {
  [key in FilterField]?: boolean
};

type Props = RouteComponentProps<MatchParams>;

type State = {
  memberList?: ListMember,
  account?: UserDTO,
  entity?: Entity,
  filter: boolean,
  filters: Filter,
  functionFilter: string[],
  searchName: string,
  loading: boolean,
  showInvitationPopin: boolean,
  showConfirmationPopin: boolean,
  showDeletePopin: boolean,
  showRenewPopin: boolean,
  currentInvitation: InvitationDTO,
  createInvitationLoading: boolean,
};

class CollaboratorsManagement extends React.Component<Props, State> {
  state: State = {
    filter: false,
    searchName: '',
    filters: {
      gestionnaire: false,
      gestionnaireDelegate: false,
      noRight: false,
    },
    functionFilter: [],
    loading: true,
    showInvitationPopin: false,
    showConfirmationPopin: false,
    showDeletePopin: false,
    showRenewPopin: false,
    currentInvitation: undefined,
    createInvitationLoading: false,
  };

  filterReference = React.createRef<HTMLButtonElement>();

  componentDidMount() {
    this.updateList();
    compteApi.fetchMyUserDetails()
      .then((user) =>
        this.setState(
          {
            account: user,
            entity: user.entities
              .filter((entity) => entity.id === this.props.match.params.entiteId)[0],
          },
        ));
  }

  updateList = () => {
    this.setState({ loading: true });
    compteApi.getMemberList(`${getInfos().sub}_${this.props.match.params.entiteId}`)
      .then((memberList) => this.setState({ memberList, loading: false }))
      .catch(() => this.setState({ loading: false }));
  };

  createInvitation = (invitationToSave: InvitationBasicDTO) => {
    this.setState({ createInvitationLoading: true });
    invitationApi.createInvitation(this.props.match.params.entiteId, invitationToSave)
      .then((response: InvitationDTO) => {
        this.addInvitation(response);
        this.handleInvitationPopin(false);
        this.openConfirmationPopin(invitationToSave, InvitationPopinEnum.CONFIRMATION);
        this.setState({ createInvitationLoading: false });
      })
      .catch(() => toastError(I18n.t('message.ERROR')))
      .finally(() => this.setState({ createInvitationLoading: false }));
  };

  deleteInvitation = () => {
    const invitationId = this.state.currentInvitation.id;
    if (!invitationId) {
      return;
    }
    invitationApi.deleteInvitation(invitationId)
      .then(() => {
        this.closeConfirmationPopin(InvitationPopinEnum.DELETE);
        this.removeInvitation(invitationId);
        toastSuccess(I18n.t('collaborators.invitation.DELETE.success'));
      })
      .catch(() => toastError(I18n.t('message.ERROR')));
  };

  renewInvitation = () => {
    const invitationId = this.state.currentInvitation.id;
    if (!invitationId) {
      return;
    }
    invitationApi.renewInvitation(invitationId)
      .then((response: InvitationDTO) => {
        this.closeConfirmationPopin(InvitationPopinEnum.RENEW);
        this.updateInvitation(response);
        toastSuccess(I18n.t('collaborators.invitation.RENEW.success'));
      })
      .catch(() => toastError(I18n.t('message.ERROR')));
  };

  removeInvitation = (invitationId: string) => {
    this.setState((prevState) => ({
      memberList: {
        ...prevState.memberList,
        invitations: prevState.memberList.invitations
          .filter((invitation) => invitation.id !== invitationId),
      },
    }));
  };

  updateInvitation = (newInvitation: InvitationDTO) => {
    this.setState((prevState) => {
      const invitations: InvitationDTO[] = prevState.memberList.invitations;
      const index = invitations
        .findIndex((invitation: InvitationDTO) => (invitation.id === newInvitation.id));
      invitations[index] = newInvitation;
      return {
        memberList: {
          ...prevState.memberList,
          invitations,
        },
      };
    });
  };

  addInvitation = (newInvitation: InvitationDTO) => {
    this.setState((prevState) => ({
      memberList: {
        ...prevState.memberList,
        invitations: prevState.memberList.invitations.concat(newInvitation),
      },
    }));
  };

  toggleFilter = () => this.setState((previousState) => ({ filter: !previousState.filter }));

  toogleFilterSelection = (filter: FilterField, value: boolean) =>
    this.setState((previousState) => ({
      filters: {
        ...previousState.filters,
        [filter]: value,
      },
    }));

  toogleFilterFunction = (filter: string, value: boolean) => {
    if (value) {
      this.setState((previousState) => ({
        functionFilter: [...previousState.functionFilter, filter],
      }));
    } else {
      this.setState((previousState) => ({
        functionFilter: previousState.functionFilter.filter((fonction) => fonction !== filter),
      }));
    }
  };

  filterInvitationByName = (invitation: InvitationDTO): boolean => {
    if (this.state.searchName === '') {
      return true;
    }
    return invitation.firstName.toLowerCase().includes(this.state.searchName.toLowerCase())
      || invitation.lastName.toLowerCase().includes(this.state.searchName.toLowerCase())
      || invitation.email.toLowerCase().includes(this.state.searchName.toLowerCase());
  };

  filterInvitationFunction = (): boolean => {
    if (this.state.filters.noRight) {
      return true;
    }
    if (!this.state.filters.gestionnaire
      && !this.state.filters.gestionnaireDelegate
      && !this.state.filters.noRight && this.state.functionFilter.length === 0) {
      return true;
    }
    return this.state.functionFilter.length > 0
      && this.state.functionFilter.includes('invitation');
  };

  filterFunction = (member: MemberDTO): boolean => {
    if (this.state.filters.gestionnaire && member.gestionnaireNaturel) {
      return true;
    }
    if (this.state.filters.gestionnaireDelegate
      && !member.gestionnaireNaturel
      && member.gestionnaire) {
      return true;
    }
    if (this.state.filters.noRight && !member.gestionnaireNaturel && !member.gestionnaire) {
      return true;
    }
    if (!this.state.filters.gestionnaire
      && !this.state.filters.gestionnaireDelegate
      && !this.state.filters.noRight && this.state.functionFilter.length === 0) {
      return true;
    }
    return this.state.functionFilter.length > 0
      && this.state.functionFilter.includes(member.fonction);
  };

  filterName = (member: MemberDTO): boolean => {
    if (this.state.searchName === '') {
      return true;
    }
    return member.nomUsuel.toLowerCase().includes(this.state.searchName.toLowerCase())
      || member.nomDaffichage.toLowerCase().includes(this.state.searchName.toLowerCase())
      || member.prenom.toLowerCase().includes(this.state.searchName.toLowerCase());
  };

  sortMembers = (memberA: MemberDTO, memberB: MemberDTO): number =>
    memberA.nomUsuel.localeCompare(memberB.nomUsuel)
    || memberA.prenom.localeCompare(memberB.prenom);

  closeDrawer = (): void => {
    this.props.history.push(COLLABORATORS_MANAGEMENT.replace(':entiteId', this.props.match.params.entiteId));
  };

  isPartenaireEntity = () => (this.state.entity
    && this.state.entity.typeEntite === TypeEntityEnum.PARTENAIRE);

  handleInvitationPopin = (show: boolean): void => {
    this.setState({ showInvitationPopin: show });
  };

  openConfirmationPopin = (currentInvitation: InvitationDTO, popinType: InvitationPopinEnum) => {
    this.handleConfirmationPopin(true, currentInvitation, popinType);
  };

  closeConfirmationPopin = (popinType: InvitationPopinEnum) => {
    this.handleConfirmationPopin(false, undefined, popinType);
  };

  handleConfirmationPopin = (
    show: boolean, currentInvitation: InvitationDTO, popinType: InvitationPopinEnum,
  ): void => {
    if (show) {
      this.setState({ currentInvitation });
    }
    this.setState((prevState) => ({
      showConfirmationPopin: popinType === InvitationPopinEnum.CONFIRMATION
        ? show : prevState.showConfirmationPopin,
      showDeletePopin: popinType === InvitationPopinEnum.DELETE ? show : prevState.showDeletePopin,
      showRenewPopin: popinType === InvitationPopinEnum.RENEW ? show : prevState.showRenewPopin,
    }));
  };

  invitationKey = (invitation: InvitationDTO): string => `${invitation.firstName}_${invitation.lastName}_${invitation.email}`;

  render(): ReactNode {
    const filteredMemberList = this.state.memberList?.members
      .filter((member) => this.filterFunction(member) && this.filterName(member)) || [];
    const filteredInvitationList = this.state.memberList?.invitations
      .filter((invitation) => this.filterInvitationFunction()
        && this.filterInvitationByName(invitation)) || [];
    const reducedFunction = this.state.memberList?.members
      .reduce((acc, value) => {
        if (acc[value.fonction]) {
          return acc;
        }
        acc[value.fonction] = 'ok';
        return acc;
      }, {}) || {};
    const gestionnaireList = filteredMemberList
      .filter((member) => member.gestionnaire && member.gestionnaireNaturel);
    const gestionnaireNaturelList = filteredMemberList
      .filter((member) => member.gestionnaire && !member.gestionnaireNaturel);
    const collaboratorList = filteredMemberList
      .filter((member) => !member.gestionnaire && !member.gestionnaireNaturel);
    const invitationList = this.state.memberList?.invitations ?? [];
    const collaboratorNumber = collaboratorList.length
      + (this.isPartenaireEntity() ? filteredInvitationList.length : 0);
    return (
      <div>
        <Switch>
          <Route
            path={COLLABORATORS_MANAGEMENT_COLLAB_INFO_DRAWER}
            render={(props) => (
              <Drawer
                onClose={this.closeDrawer}
                isOpen
                noFill
              >
                <CollaboratorsInformation
                  entityId={props.match.params.entiteId}
                  onClose={this.closeDrawer}
                />
              </Drawer>
            )}
          />
        </Switch>
        <div className="navigation-header">
          <NavigationBackButton url={HOME_PAGE_ROUTE} i18nKey="collaborators.back" image={collaborators} />
          <div className="button-container">
            <SearchButton
              placeholder={I18n.t('SEARCH.NAME_TEL')}
              onChange={(searchName) => this.setState({ searchName })}
            />
            <button
              className="icon-button with-tooltip"
              type="button"
              ref={this.filterReference}
              onClick={this.toggleFilter}
            >
              <img alt="button-leave" src={this.state.filter ? btnFilterOuvert : btnFilter} />
              <div className="tooltip">
                <div className="arrow-up" />
                <div className="tooltip-content">
                  <Translate
                    value="collaborators.tooltip.filters"
                  />
                </div>
              </div>
            </button>
            <CloseableComponent
              isOpened={this.state.filter}
              toggle={this.toggleFilter}
              buttonRef={this.filterReference.current}
            >
              <div>
                <div className="filter-category">
                  <Translate value="collaborators.rights" />
                </div>
                <BasicCheckBox
                  name="gestionnaire"
                  onChange={(e) => {
                    this.toogleFilterSelection('gestionnaire', e.target.checked);
                  }}
                  placeholder={I18n.t('collaborators.gestionnaires', {
                    number: this.state.memberList?.members
                      .filter((member) => member.gestionnaireNaturel)
                      .length,
                  })}
                  value={this.state.filters.gestionnaire}
                />
                <BasicCheckBox
                  name="gestionnaireDeleguate"
                  onChange={(e) => {
                    this.toogleFilterSelection('gestionnaireDelegate', e.target.checked);
                  }}
                  placeholder={I18n.t('collaborators.gestionnaireDelegate', {
                    number: this.state.memberList?.members
                      .filter((member) => member.gestionnaire && !member.gestionnaireNaturel)
                      .length,
                  })}
                  value={this.state.filters.gestionnaireDelegate}
                />
                <BasicCheckBox
                  name="noRight"
                  onChange={(e) => {
                    this.toogleFilterSelection('noRight', e.target.checked);
                  }}
                  placeholder={I18n.t('collaborators.noRight', {
                    number: this.state.memberList?.members
                      .filter((member) => !member.gestionnaireNaturel && !member.gestionnaire)
                      .length,
                  })}
                  value={this.state.filters.noRight}
                />
                <div className="filter-category">
                  <Translate value="collaborators.fonction" />
                </div>
                {
                  Object.keys(reducedFunction).map((fonction) => (
                    <BasicCheckBox
                      key={fonction}
                      name={fonction}
                      onChange={(e) => {
                        this.toogleFilterFunction(fonction, e.target.checked);
                      }}
                      placeholder={`${fonction} (${this.state.memberList?.members
                        .filter((member) => member.fonction === fonction)
                        .length})`}
                      value={this.state.functionFilter.includes(fonction)}
                    />
                  ))
                }
                {
                  this.isPartenaireEntity() && invitationList.length > 0 && (
                    <BasicCheckBox
                      key="invitation"
                      name="invitation"
                      onChange={(e) => {
                        this.toogleFilterFunction('invitation', e.target.checked);
                      }}
                      placeholder={`Invitations (${invitationList.length})`}
                      value={this.state.functionFilter.includes('invitation')}
                    />
                  )
                }
              </div>
            </CloseableComponent>
            <button
              className="icon-button with-tooltip"
              type="button"
              onClick={
                () => this.props.history
                  .push({
                    pathname: COLLABORATORS_MANAGEMENT_COLLAB_INFO_DRAWER.replace(':entiteId', this.props.match.params.entiteId),
                    state: { isManager: this.state.entity.rattachement.manager },
                  })
              }
            >
              <img alt="button-leave" src={btnInfo} />
              <div className="tooltip">
                <div className="arrow-up" />
                <div className="tooltip-content">
                  <Translate
                    value="collaborators.tooltip.infos"
                  />
                </div>
              </div>
            </button>
          </div>
        </div>
        <div className="rattachement-container">
          <div className="rattachement-content">
            {
              this.state.memberList ? (
                <>
                  <div className="title-header">
                    <H4Title className="rattachement-title">
                      <Translate
                        value="collaborators.title"
                        name={getStructureName(this.state.entity)}
                        number={this.state.memberList.members.length}
                      />
                    </H4Title>
                    {
                      this.isPartenaireEntity() && (
                        <Button
                          type="primary"
                          i18nKey="collaborators.invitation.button"
                          onClick={() => this.handleInvitationPopin(true)}
                        />
                      )
                    }
                  </div>
                  <div className="subtitle-bold category-header">
                    <Translate
                      value="collaborators.gestionnaires"
                      number={gestionnaireList.length}
                    />
                  </div>
                  <div className="subtitle-blue">
                    <Translate value="collaborators.gestionnaireDesc" />
                  </div>
                  <div className="rattachement-demande-container">
                    {
                      gestionnaireList
                        .sort(this.sortMembers)
                        .map((member) => (
                          <GestionnaireTile key={member.idn} member={member} />
                        ))
                    }
                  </div>
                  <div className="subtitle-bold category-header">
                    <Translate
                      value="collaborators.gestionnaireDelegate"
                      number={gestionnaireNaturelList.length}
                    />
                  </div>
                  <div className="subtitle-blue">
                    <Translate value="collaborators.gestionnaireDelegateDesc" />
                  </div>
                  <div className="collaborator-management-container">
                    {
                      gestionnaireNaturelList
                        .sort(this.sortMembers)
                        .map((member) => (
                          <CollaboratorTile
                            updateList={this.updateList}
                            key={member.idn}
                            member={member}
                            account={this.state.account}
                            entity={this.state.entity}
                          />
                        ))
                    }
                  </div>
                  <div className="subtitle-bold category-header">
                    <Translate
                      value="collaborators.collaborators"
                      number={collaboratorNumber}
                    />
                  </div>
                  <div className="subtitle-blue">
                    <Translate value="collaborators.collaboratorsDesc" />
                  </div>
                  <div className="collaborator-management-container">
                    {
                      this.isPartenaireEntity && filteredInvitationList
                        .map((invitation) => (
                          <InvitationTile
                            entity={this.state.entity}
                            invitation={invitation}
                            key={this.invitationKey(invitation)}
                            clickDelete={() => this.openConfirmationPopin(
                              invitation, InvitationPopinEnum.DELETE,
                            )}
                            clickRenew={() => this.openConfirmationPopin(
                              invitation, InvitationPopinEnum.RENEW,
                            )}
                          />
                        ))
                    }
                    {
                      collaboratorList
                        .sort(this.sortMembers)
                        .map((member) => (
                          <CollaboratorTile
                            updateList={this.updateList}
                            key={member.idn}
                            member={member}
                            account={this.state.account}
                            entity={this.state.entity}
                          />
                        ))
                    }
                  </div>
                </>
              ) : this.state.loading && (
                <div className="loader-container">
                  <Loader />
                </div>
              )
            }
          </div>
          <InvitationModal
            show={this.state.showInvitationPopin}
            close={() => this.handleInvitationPopin(false)}
            createInvitation={this.createInvitation}
            isLoading={this.state.createInvitationLoading}
          />
          <ConfirmationModal
            show={this.state.showConfirmationPopin}
            close={() => this.closeConfirmationPopin(InvitationPopinEnum.CONFIRMATION)}
            entityName={this.state.entity?.name}
            currentInvitation={this.state.currentInvitation}
            popinType={InvitationPopinEnum.CONFIRMATION}
          />
          <ConfirmationModal
            show={this.state.showDeletePopin}
            close={() => this.closeConfirmationPopin(InvitationPopinEnum.DELETE)}
            entityName={this.state.entity?.name}
            currentInvitation={this.state.currentInvitation}
            popinType={InvitationPopinEnum.DELETE}
            handleClick={this.deleteInvitation}
          />
          <ConfirmationModal
            show={this.state.showRenewPopin}
            close={() => this.closeConfirmationPopin(InvitationPopinEnum.RENEW)}
            entityName={this.state.entity?.name}
            currentInvitation={this.state.currentInvitation}
            popinType={InvitationPopinEnum.RENEW}
            handleClick={this.renewInvitation}
          />
        </div>
        <Footer />
      </div>
    );
  }
}

export default withRouter(CollaboratorsManagement);
